/**
 * rikumo-lowercase.js
 * ---------------------------------------------------------------------------
 * Forces the brand name to ALWAYS render lowercase ("rikumo") on the
 * storefront — regardless of how editors typed it (Rikumo, RIKUMO, RiKumo…).
 *
 * Normalization happens at DISPLAY TIME only; stored content is never changed.
 * Only the letter CASE is altered, so the character count stays identical and
 * there is no layout shift (CLS-safe).
 *
 * ── HOW TO CHANGE THE BRAND WORD ───────────────────────────────────────────
 * Edit the single constant `BRAND_WORD` below. It is matched case-insensitively
 * as a whole word, so changing it to e.g. "acme" is all that is required.
 * ---------------------------------------------------------------------------
 */
(function () {
  'use strict';

  /** The brand word to normalize (lowercase target). Change this only. */
  var BRAND_WORD = 'rikumo';

  // Whole-word, case-insensitive. \b boundaries keep us from touching
  // substrings inside unrelated words (e.g. "rikumonster" is left alone),
  // while still catching possessives like "Rikumo's" — the apostrophe is a
  // word boundary so the captured group is just the brand word itself.
  var BRAND_RE = new RegExp('\\b' + BRAND_WORD.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '\\b', 'gi');
  var LOWER = BRAND_WORD.toLowerCase();

  // Tags whose text content must never be touched.
  var SKIP_TAGS = {
    SCRIPT: true,
    STYLE: true,
    NOSCRIPT: true,
    TEXTAREA: true,
    INPUT: true,
    SELECT: true,
    OPTION: true,
    CODE: true,
    PRE: true,
  };

  /**
   * Decide whether a text node is safe to process.
   * Skips empty nodes, nodes that do not contain the brand word, nodes inside
   * skipped tags, and any node living under a contenteditable element.
   */
  function shouldProcess(textNode) {
    if (!textNode.nodeValue) return false;
    // Quick bail: nothing to do if the brand word can't be present.
    BRAND_RE.lastIndex = 0;
    if (!BRAND_RE.test(textNode.nodeValue)) return false;

    var el = textNode.parentNode;
    while (el && el.nodeType === Node.ELEMENT_NODE) {
      if (SKIP_TAGS[el.tagName]) return false;
      if (el.isContentEditable) return false;
      el = el.parentNode;
    }
    return true;
  }

  /** Replace brand occurrences in a single text node (case only). */
  function processTextNode(textNode) {
    if (!shouldProcess(textNode)) return;
    BRAND_RE.lastIndex = 0;
    var next = textNode.nodeValue.replace(BRAND_RE, LOWER);
    // Only write back if something actually changed — avoids triggering the
    // observer with a no-op mutation (guards against double-processing loops).
    if (next !== textNode.nodeValue) {
      textNode.nodeValue = next;
    }
  }

  /** Walk all text nodes under `root` and normalize each one. */
  function processSubtree(root) {
    if (!root) return;
    if (root.nodeType === Node.TEXT_NODE) {
      processTextNode(root);
      return;
    }
    if (root.nodeType !== Node.ELEMENT_NODE && root.nodeType !== Node.DOCUMENT_FRAGMENT_NODE) {
      return;
    }
    // If the root element itself is a skip tag, ignore the whole subtree.
    if (root.nodeType === Node.ELEMENT_NODE && SKIP_TAGS[root.tagName]) return;

    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
      acceptNode: function (node) {
        return shouldProcess(node) ? NodeFilter.FILTER_ACCEPT : NodeFilter.FILTER_REJECT;
      },
    });
    var batch = [];
    var node;
    while ((node = walker.nextNode())) {
      batch.push(node);
    }
    // Mutate after walking so we don't invalidate the live TreeWalker.
    for (var i = 0; i < batch.length; i++) {
      processTextNode(batch[i]);
    }
  }

  // ── MutationObserver: catch content injected after first paint ────────────
  // (AJAX cart drawer, quick add, predictive search, Section Rendering API.)
  var pending = [];
  var scheduled = false;

  function flush() {
    scheduled = false;
    var nodes = pending;
    pending = [];
    for (var i = 0; i < nodes.length; i++) {
      processSubtree(nodes[i]);
    }
  }

  // Debounce so a burst of mutations coalesces into a single normalization pass.
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    if (window.requestAnimationFrame) {
      window.requestAnimationFrame(flush);
    } else {
      window.setTimeout(flush, 16);
    }
  }

  function onMutations(mutations) {
    for (var i = 0; i < mutations.length; i++) {
      var m = mutations[i];
      if (m.type === 'childList') {
        for (var j = 0; j < m.addedNodes.length; j++) {
          pending.push(m.addedNodes[j]);
        }
      } else if (m.type === 'characterData') {
        pending.push(m.target);
      }
    }
    if (pending.length) schedule();
  }

  function start() {
    // Initial pass over everything already in the document.
    processSubtree(document.body);

    if (!('MutationObserver' in window)) return;
    var observer = new MutationObserver(onMutations);
    observer.observe(document.body, {
      subtree: true,
      childList: true,
      characterData: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
